import { Balance } from '@codex-data/sdk';
import { useQuery } from '@tanstack/react-query';

import { SupportedChainIDs } from '@/constants/chain';

import { fetcherWithJwt } from '@/utils/fetcher';
import { ChainAddress } from '@/utils/models';
import { routes } from '@/utils/routes';

interface TokenFilter {
  address: ChainAddress;
  chainId: number;
}

interface UseBalancesParams {
  wallets: ChainAddress[];
  chainIds?: SupportedChainIDs[];
  /** Optional list of specific tokens to filter balances for. */
  tokens?: TokenFilter[];
}

export const balancesQueryKey = ({ wallets, chainIds, tokens }: UseBalancesParams) =>
  ['balances', wallets, chainIds, tokens] as const;

/** Fetches token balances (including native assets) for the given wallets across specified chains. */
export const useBalances = ({ wallets, chainIds, tokens }: UseBalancesParams) => {
  const params = new URLSearchParams();
  params.set('wallets', wallets.join(','));
  if (chainIds?.length) {
    params.set('chainIds', chainIds.join(','));
  }
  if (tokens?.length) {
    params.set('tokens', tokens.map((t) => `${t.address}:${t.chainId}`).join(','));
  }

  const url = `${routes.api.BALANCES}?${params.toString()}`;

  return useQuery<Balance[], Error>({
    queryKey: balancesQueryKey({ wallets, chainIds, tokens }),
    queryFn: () => fetcherWithJwt(url),
    enabled: wallets.length > 0,
    staleTime: 30_000,
  });
};
